import { Instagram, Facebook, Linkedin, Mail } from "lucide-react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { TEST_IDS } from "@/constants/testIds";
import { useT } from "@/i18n/LanguageContext";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";

const WHATSAPP_LINK = "https://wa.me/34647931239";

const navLinks = [
  { key: "theHouse", id: "the-house" },
  { key: "expertFocus", id: "expert-focus" },
  { key: "process", id: "process" },
  { key: "offering", id: "offering" },
  { key: "trust", id: "trust" },
  { key: "privateInquiry", id: "contact" },
];

export const Footer = () => {
  const { t } = useT();
  const year = new Date().getFullYear();
  const location = useLocation();
  const navigate = useNavigate();

  const navigateToSection = (id) => {
    if (location.pathname !== "/") {
      navigate(`/#${id}`);
      return;
    }
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <footer
      data-testid={TEST_IDS.footer.root}
      className="relative bg-ink text-white pt-24 md:pt-32 pb-12 overflow-hidden"
    >
      <div className="absolute inset-x-0 top-0 gold-divider" />
      <div className="absolute inset-0 pointer-events-none opacity-10">
        <div className="absolute inset-0 blueprint-bg" />
      </div>

      <div className="container-luxe relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 lg:gap-16">
          <div className="md:col-span-5">
            <img
              src="/ck-logo-white.svg"
              alt="Coumba Kandji Diamonds"
              className="h-16 md:h-20 w-auto object-contain"
            />
            <p className="mt-8 max-w-sm text-sm leading-relaxed text-white/65 font-light">
              {t("footer.tagline")}
            </p>

            {/* Language switcher — accessible on all breakpoints */}
            <div className="mt-8 inline-flex items-center gap-3 bg-white/5 border border-white/10 px-3 py-1 rounded-full">
              <LanguageSwitcher variant="dark" direction="up" />
            </div>
          </div>

          <div className="md:col-span-3">
            <div className="overline">{t("footer.contact")}</div>
            <ul className="mt-5 space-y-5 text-sm text-white/75 font-light">
              <li>
                <div className="overline-soft text-white/50 mb-1.5">
                  {t("footer.directInquiries")}
                </div>
                <a
                  data-testid={TEST_IDS.footer.email}
                  href="mailto:info@coumbakandji.com"
                  className="hover:text-gold_soft transition-colors"
                >
                  info@coumbakandji.com
                </a>
              </li>
              <li>
                <div className="overline-soft text-white/50 mb-1.5">
                  {t("footer.phone")}
                </div>
                <a
                  href={WHATSAPP_LINK}
                  target="_blank"
                  rel="noreferrer"
                  className="block hover:text-gold_soft transition-colors"
                >
                  {t("footer.internationalLabel")}: +34 647 931 239
                </a>
                <a
                  href="https://wa.me/221781601145"
                  target="_blank"
                  rel="noreferrer"
                  className="block hover:text-gold_soft transition-colors"
                >
                  {t("footer.senegalLabel")}: +221 78 160 11 45
                </a>
              </li>
              <li>
                <div className="overline-soft text-white/50 mb-1.5">
                  {t("footer.headquarters")}
                </div>
                <p className="leading-relaxed">
                  {t("inquiry.addressL1")}
                  <br />
                  {t("inquiry.addressL2")}
                </p>
              </li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline">{t("footer.navigate")}</div>
            <ul className="mt-5 space-y-3 text-sm text-white/75 font-light">
              {navLinks.map((l) => (
                <li key={l.id}>
                  <button
                    onClick={() => navigateToSection(l.id)}
                    className="hover:text-gold_soft transition-colors text-left"
                  >
                    {l.key === "privateInquiry"
                      ? t("footer.privateInquiry")
                      : t(`nav.${l.key}`)}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline">{t("footer.follow")}</div>
            <ul className="mt-5 space-y-3 text-sm text-white/75 font-light">
              <li>
                <a
                  data-testid={TEST_IDS.footer.instagram}
                  href="https://www.instagram.com/coumbakandjidiamonds/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-gold_soft transition-colors"
                >
                  <Instagram className="w-4 h-4" strokeWidth={1.4} />
                  Instagram
                </a>
              </li>
              <li>
                <a
                  data-testid={TEST_IDS.footer.facebook}
                  href="https://www.facebook.com/profile.php?id=61576539206575"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-gold_soft transition-colors"
                >
                  <Facebook className="w-4 h-4" strokeWidth={1.4} />
                  Facebook
                </a>
              </li>
              <li>
                <a
                  data-testid={TEST_IDS.footer.linkedin}
                  href="https://www.linkedin.com/company/coumba-kandji-diamonds/"
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-2 hover:text-gold_soft transition-colors"
                >
                  <Linkedin className="w-4 h-4" strokeWidth={1.4} />
                  LinkedIn
                </a>
              </li>
              <li>
                <a
                  href="mailto:info@coumbakandji.com"
                  className="inline-flex items-center gap-2 hover:text-gold_soft transition-colors"
                >
                  <Mail className="w-4 h-4" strokeWidth={1.4} />
                  Email
                </a>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-12 flex flex-col md:flex-row md:items-center md:justify-between gap-6 pt-8 border-t border-white/10">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            © {year} Coumba Kandji Diamonds · {t("footer.allRights")}
          </p>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-2 font-mono text-[10px] uppercase tracking-[0.3em] text-white/50">
            <Link
              data-testid="footer-link-legal"
              to="/legal-notice"
              className="hover:text-gold_soft transition-colors"
            >
              {t("footer.legal")}
            </Link>
            <Link
              data-testid={TEST_IDS.footer.privacy}
              to="/privacy-policy"
              className="hover:text-gold_soft transition-colors"
            >
              {t("footer.privacy")}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};
